// Contact Center number formatting and target colouring — one place, so a
// figure reads the same on every page.
//
// A null value is "no figure" (no calls to average, nothing to compare with)
// and shows as an em dash, never as 0.
import { c } from '../../theme';

const DASH = '—';

export const fmtCount = (n) => (n == null ? DASH : Number(n).toLocaleString('en-US'));

export const fmtPct = (n) => (n == null ? DASH : `${Number(n).toFixed(1)}%`);

export const fmtMoney = (n) => (n == null ? DASH : `$${Number(n).toFixed(2)}`);

// The old app formatted durations three ways, page by page, and each page here
// uses the one its old counterpart did:
//
//   fmtMmSs    "3:56", "632:32" — unpadded, never hours. Exec and Board (the
//              old ownership and board views).
//   fmtClock   "03:56", "10:32:32" — padded, hours from one hour. Live Ops,
//              Agents, Director/Manager/Supervisor, Email, VP, Outbound (the
//              old manager/hooks.js fmtMmSs, re-exported to all of those).
//   fmtElapsed "41s", "1m 23s", "1h 5m", "6d 9h" — time in a status on the
//              live roster (the old fmtDuration).

// Seconds as M:SS, unpadded.
export function fmtMmSs(secs) {
  if (secs == null) return DASH;
  const total = Math.round(Number(secs));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Seconds as MM:SS, or H:MM:SS from an hour. Zero is "00:00".
export function fmtClock(secs) {
  if (secs == null || Number.isNaN(Number(secs))) return DASH;
  const total = Math.max(0, Math.round(Number(secs)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

// Elapsed time, the largest two units: days and hours past a day, so an agent
// logged off for weeks reads "6d 9h" rather than "153h".
export function fmtElapsed(secs) {
  if (secs == null || Number.isNaN(Number(secs))) return DASH;
  const total = Math.max(0, Math.round(Number(secs)));
  if (total === 0) return '0s';
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// A change with its sign and unit: "+1.2 pts", "−3.4%", "+0:12".
export function fmtChange(value, unit) {
  if (value == null) return DASH;
  const sign = value > 0 ? '+' : value < 0 ? '−' : '±';
  const abs = Math.abs(value);
  if (unit === 'pts') return `${sign}${abs.toFixed(1)} pts`;
  if (unit === '%') return `${sign}${abs.toFixed(1)}%`;
  if (unit === 's') return `${sign}${fmtMmSs(abs)}`;
  if (unit === '$') return `${sign}$${abs.toFixed(2)}`;
  return `${sign}${abs}`;
}

// One colour rule per metric (Phase 0, decision 1). Returns 'good' | 'watch' |
// 'bad' | null (no target, or no figure).
export const STATUS = {
  serviceLevel: (v) => (v == null ? null : v >= 95 ? 'good' : v >= 90 ? 'watch' : 'bad'),
  aht: (v) => (v == null ? null : v <= 300 ? 'good' : v <= 360 ? 'watch' : 'bad'),
  // The old app's ASA bands: on target at 30 seconds, amber to 45.
  asa: (v) => (v == null ? null : v <= 30 ? 'good' : v <= 45 ? 'watch' : 'bad'),
  abandonPct: (v) => (v == null ? null : v <= 3 ? 'good' : v <= 4.5 ? 'watch' : 'bad'),
  // The old app's agent bands (ui/thresholds.js EFFICIENCY).
  // The old staffing view's live adherence bands.
  adherence: (v) => (v == null ? null : v >= 90 ? 'good' : v >= 85 ? 'watch' : 'bad'),
  efficiency: (v) => (v == null ? null : v >= 92 ? 'good' : v >= 87 ? 'watch' : 'bad'),
  // Fewer than three evaluations is too few to judge: shown, but neutral.
  qa: (v, n) => (v == null || !(n >= 3) ? null : v >= 90 ? 'good' : v >= 80 ? 'watch' : 'bad'),
};

// Hours and minutes: "7h 05m", "45m". Minutes are floored so the hour never
// reads "60m" (the old drill-in rounded, and showed "1h 60m").
export function fmtHm(secs) {
  if (secs == null) return DASH;
  const total = Math.max(0, Math.round(Number(secs)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

export const statusColor = (s) => ({
  good: c.successText, watch: c.warningText, bad: c.dangerText,
}[s] || c.textStrong);

export const statusTint = (s) => ({
  good: c.successTint, watch: c.warningTint, bad: c.dangerTint,
}[s] || c.surfaceSunk);

// Whether a change is an improvement: 'up' means higher is better, 'down'
// lower, null means there is no agreed direction (volume) and it stays grey.
export function changeTone(value, better) {
  if (value == null || value === 0 || !better) return null;
  return (better === 'up') === (value > 0) ? 'good' : 'bad';
}

export function fmtAsOf(iso) {
  if (!iso) return null;
  // Intl THROWS on an invalid date rather than returning a dash, so an `asOf`
  // of the wrong shape takes the whole page down over a cosmetic timestamp.
  // That is exactly what happened to the Exec and VP pages: both handed this
  // the raw {job: time} object instead of a time, and both rendered blank.
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Los_Angeles',
  }).format(at);
}

/**
 * The one timestamp to print for a page fed by several ingest jobs.
 *
 * The server sends `asOf` as {job: finishedAt} — one entry per pull — and the
 * honest headline is the OLDEST of them: nothing on the page is fresher than
 * the stalest feed it was built from. Every Contact Center page that shows
 * "Data as of" goes through here, because three pages each doing their own
 * reduce is how two of them came to hand the raw object to a formatter.
 */
export function oldestAsOf(asOf) {
  if (!asOf) return null;
  if (typeof asOf === 'string' || asOf instanceof Date) return asOf;
  const times = Object.values(asOf).filter(Boolean);
  return times.length ? times.reduce((a, b) => (a < b ? a : b)) : null;
}
